/**
 * Zentrale Button-Definition ("Button-Set").
 *
 * Alle Buttons der Seite holen ihre Grundform hier ab. Wer künftig die
 * Eckenrundung, die Größen oder das Grundverhalten ändern will, ändert
 * es an genau dieser Stelle - und nicht in 30 Dateien.
 *
 * Die Rundung selbst steckt in der CSS-Variable `--btn-radius`
 * (siehe src/index.css). Ein Wert, alle Buttons.
 */

export type ButtonVariant = "solid" | "outline" | "ghost";
export type ButtonSize = "xs" | "sm" | "md" | "lg";
export type ButtonShape = "soft" | "pill" | "circle";

/** Grundverhalten, das jeder Button teilt. */
export const BUTTON_BASE = [
  "inline-flex items-center justify-center gap-2",
  "whitespace-nowrap select-none",
  "font-medium transition-colors duration-200",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current focus-visible:ring-offset-2",
  "disabled:cursor-not-allowed disabled:opacity-60",
  "cursor-pointer",
].join(" ");

/**
 * Formen:
 * - soft   = Standard, leicht abgerundete Ecken (Regler: --btn-radius)
 * - pill   = volle Kapsel, nur wenn ausdrücklich gewünscht
 * - circle = runder Icon-Button
 */
export const BUTTON_SHAPES: Record<ButtonShape, string> = {
  soft: "rounded-[var(--btn-radius)]",
  pill: "rounded-full",
  circle: "rounded-full aspect-square",
};

/** Größen: Höhe, Innenabstand und Schriftgröße. */
export const BUTTON_SIZES: Record<ButtonSize, string> = {
  xs: "px-3 py-1.5 text-[11px]",
  sm: "px-4 py-2 text-xs",
  md: "px-6 py-3 text-sm",
  lg: "px-7 py-3.5 text-sm",
};

/**
 * Varianten beschreiben nur die Struktur (Rahmen/Fläche).
 * Die Farben kommen bewusst aus der jeweiligen Seite, weil jede
 * Seitenfamilie ihre eigene Farbwelt hat (Grün, Blau, Beige, Orange).
 */
export const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  solid: "border border-transparent",
  outline: "border",
  ghost: "border border-transparent",
};